import { CANVAS_W, CANVAS_H } from './constants';

// ── Chat ──────────────────────────────────────────────────────────────────────
export function formatChatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() || '?';
}

export function avatarBg(name = '') {
  const colors = ['#1075BA', '#8b4fcc', '#00a882', '#e67e22', '#e63946', '#2d9e4e'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

// ── Header helpers ────────────────────────────────────────────────────────────
export function getEngInitials(name = '') {
  return name.split(' ').slice(0, 2).map(n => (n[0] || '').toUpperCase()).join('') || '?';
}

// ── Video / iframe URL resolvers ──────────────────────────────────────────────
export function resolveVideoUrl(provider, url) {
  if (!url) return null;
  if (provider === 'youtube') {
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
    );
    const id = match?.[1] || (url.length === 11 ? url : null);
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : url;
  }
  if (provider === 'vimeo') {
    const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    const id = match?.[1] || (/^\d+$/.test(url) ? url : null);
    return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : url;
  }
  return url;
}

export function resolveIframeSrc(embedCode) {
  if (!embedCode) return null;
  const srcMatch = embedCode.match(/src=["']([^"']+)["']/i);
  if (srcMatch) return srcMatch[1];
  if (/^https?:\/\//i.test(embedCode.trim())) return embedCode.trim();
  return null;
}

// ── Hotspot geometry ──────────────────────────────────────────────────────────
export function getHotspotCentroid(h) {
  const pxX = p => (p / 100) * CANVAS_W;
  const pxY = p => (p / 100) * CANVAS_H;
  if (h.shape === 'rect' && h.points?.[0] && h.meta) {
    const x = pxX(h.points[0].x), y = pxY(h.points[0].y);
    return { cx: x + pxX(h.meta.width || 10) / 2, cy: y + pxY(h.meta.height || 10) / 2 };
  }
  if (h.shape === 'circle' && h.points?.[0]) {
    return { cx: pxX(h.points[0].x), cy: pxY(h.points[0].y) };
  }
  const pts = h.points || [];
  if (!pts.length) return { cx: CANVAS_W / 2, cy: CANVAS_H / 2 };
  const xs = pts.map(p => pxX(p.x)), ys = pts.map(p => pxY(p.y));
  return {
    cx: (Math.min(...xs) + Math.max(...xs)) / 2,
    cy: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

// ── Hotspot / element deserializers ───────────────────────────────────────────
export function deserializeHotspot(h) {
  // If already deserialized (hotspotType present), return as-is.
  if (h.hotspotType) return h;
  const a = h.action || {};
  let hotspotType = 'link';
  let hotspotAction = {};

  if (a.type === 'chat') {
    hotspotType = 'chat';
    hotspotAction = {
      channelId: a.pageId || null,
      mode: h.meta?.mode || 'group',
      chatTitle: h.meta?.chatTitle || '',
      panelWidth: h.meta?.panelWidth || 'md',
      showParticipantCount: h.meta?.showParticipantCount !== false,
    };
  } else if (a.type === 'stream' && h.meta?._isLivestreamChat) {
    hotspotType = 'livestream-chat';
    hotspotAction = {
      channelId: h.meta?.channelId || null,
      chatTitle: h.meta?.chatTitle || 'Livestream Chat',
    };
  } else if (a.type === 'stream') {
    hotspotType = 'stream';
    hotspotAction = {
      streamType:    h.meta?.streamType    || 'iframe',
      streamUrl:     h.meta?.streamUrl     || '',
      viewerCount:   h.meta?.viewerCount   || 0,
      muxStreamId:   h.meta?.muxStreamId   || null,
      muxStreamKey:  h.meta?.muxStreamKey  || null,
      muxRtmpUrl:    h.meta?.muxRtmpUrl    || null,
      muxPlaybackId: h.meta?.muxPlaybackId || null,
      muxStatus:     h.meta?.muxStatus     || null,
    };
  } else if (a.type === 'modal' && h.meta?.galleryImages) {
    hotspotType = 'gallery';
    hotspotAction = { images: h.meta.galleryImages };
  } else if (a.type === 'modal') {
    hotspotType = 'iframe';
    hotspotAction = {
      embedCode: a.url || '',
      modalWidth: h.meta?.modalWidth || 'md',
    };
  } else if (a.type === 'url' && h.meta?._isVideo) {
    hotspotType = 'video';
    hotspotAction = {
      videoProvider: h.meta?.videoProvider || 'url',
      videoUrl: a.url || '',
      autoplay: h.meta?.autoplay || false,
      controls: h.meta?.controls !== false,
    };
  } else if (a.type === 'url') {
    hotspotType = 'link';
    hotspotAction = { linkTarget: 'external', externalUrl: a.url || '', openIn: 'same' };
  } else if (a.type === 'image' || h.meta?._isImage) {
    hotspotType = 'image';
    hotspotAction = {
      linkTarget:          h.meta?._imageLinkTarget          || null,
      targetId:            h.meta?._imageTargetId            || null,
      externalUrl:         h.meta?._imageExternalUrl         || null,
      transitionVideoUrl:  h.meta?._imageTransitionVideoUrl  || null,
    };
  } else if (a.type === 'text' || h.meta?._isText) {
    hotspotType = 'text';
    hotspotAction = {};
  } else {
    hotspotType = 'link';
    hotspotAction = {
      linkTarget: 'lobby',
      targetId: a.pageId || null,
      openIn: 'same',
      transitionVideoUrl: h.meta?.transitionVideoUrl || null,
    };
  }

  return { ...h, hotspotType, hotspotAction };
}

export function deserializeElement(el) {
  // If already deserialized (hotspotType present), return as-is.
  if (el.hotspotType) return el;
  if (el.elementType === 'exhibitor-grid') {
    return {
      ...el,
      hotspotType:   'exhibitor-grid',
      hotspotAction: {
        exhibitionId: el.config?.exhibitionId || null,
        lobbyId:      el.config?.lobbyId      || null,
      },
      shape:    'rect',
      points:   el.points || [],
      meta:     el.meta   || {},
      isActive: el.isActive !== false,
    };
  }
  if (el.elementType === 'livestream-chat') {
    return {
      ...el,
      hotspotType: 'livestream-chat',
      hotspotAction: {
        channelId: el.config?.channelId || null,
        chatTitle: el.config?.chatTitle || 'Livestream Chat',
      },
      shape:    'rect',
      points:   el.points || [],
      meta:     el.meta   || {},
      isActive: el.isActive !== false,
    };
  }
  return {
    ...el,
    hotspotType: el.elementType || 'stream',
    hotspotAction: {
      streamType:    el.config?.streamType    || 'iframe',
      streamUrl:     el.config?.streamUrl     || '',
      viewerCount:   el.config?.viewerCount   || 0,
      hideUI:        el.config?.hideUI        || false,
      muxStreamId:   el.config?.muxStreamId   || null,
      muxStreamKey:  el.config?.muxStreamKey  || null,
      muxRtmpUrl:    el.config?.muxRtmpUrl    || null,
      muxPlaybackId: el.config?.muxPlaybackId || null,
      muxStatus:     el.config?.muxStatus     || null,
    },
    shape:    'rect',
    points:   el.points || [],
    meta:     el.meta   || {},
    isActive: el.isActive !== false,
  };
}